/**
 * Conversiones sRGB ⇄ OKLab ⇄ OKLCH (Björn Ottosson), ajuste a gamut y
 * contraste WCAG. Sin dependencias: se puede usar en Node, en un worker o
 * en el navegador.
 */
import type { RGB, Color, Rng } from './types';

export const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
export const srgbToLinear = (c: number) => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
export const linearToSrgb = (c: number) => c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;

export function oklabToLinear(L: number, a: number, b: number): RGB {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
  const l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s
  ];
}
export function linearToOklab(r: number, g: number, b: number): RGB {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s
  ];
}
export const inGamut = (rgb: number[]) => rgb.every(v => v >= -0.0005 && v <= 1.0005);

/** Reduce el croma por bisección hasta entrar en el gamut sRGB: evita el
 *  recorte por canal, que rompe el matiz y produce bandas. */
export function oklchToRgb(L: number, C: number, h: number) {
  const rad = h * Math.PI / 180;
  let lo = 0, hi = C, lin = oklabToLinear(L, C * Math.cos(rad), C * Math.sin(rad));
  let clipped = false;
  if (!inGamut(lin)) {
    clipped = true;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      lin = oklabToLinear(L, mid * Math.cos(rad), mid * Math.sin(rad));
      if (inGamut(lin)) lo = mid; else hi = mid;
    }
    lin = oklabToLinear(L, lo * Math.cos(rad), lo * Math.sin(rad));
  }
  const out = lin.map(v => Math.round(clamp(linearToSrgb(clamp(v, 0, 1)), 0, 1) * 255));
  return { rgb: [out[0], out[1], out[2]] as RGB, clipped, C: clipped ? lo : C };
}
export function rgbToOklch(r: number, g: number, b: number) {
  const [L, a, bb] = linearToOklab(srgbToLinear(r / 255), srgbToLinear(g / 255), srgbToLinear(b / 255));
  const C = Math.hypot(a, bb);
  let h = Math.atan2(bb, a) * 180 / Math.PI;
  if (h < 0) h += 360;
  return { L, C, h };
}
export const hex = ([r, g, b]: RGB) => '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase();

/** Color de trabajo: guarda OKLCH y su sRGB ya ajustado a gamut. */
export function mk(L: number, C: number, h: number, role: string): Color {
  L = clamp(L, 0, 1); C = Math.max(0, C); h = ((h % 360) + 360) % 360;
  const f = oklchToRgb(L, C, h);
  return { L, C: f.C, h, role, rgb: f.rgb, hex: hex(f.rgb), clipped: f.clipped };
}
export const css = (c: Color) => `rgb(${c.rgb[0]},${c.rgb[1]},${c.rgb[2]})`;

/* Luminancia relativa y contraste WCAG 2.x */
export function relLum([r, g, b]: RGB): number {
  const f = (v: number) => srgbToLinear(v / 255);
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
export const contrast = (a: RGB, b: RGB) => {
  const l1 = relLum(a), l2 = relLum(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

/** Interpolación en OKLCH por el arco corto de matiz.
 *  Esta es la política central: mezclar en sRGB atraviesa una zona
 *  desaturada («barro»); en OKLCH el croma se mantiene todo el recorrido. */
export function mix(a: Color, b: Color, t: number): Color {
  let dh = b.h - a.h;
  if (dh > 180) dh -= 360;
  if (dh < -180) dh += 360;
  return mk(a.L + (b.L - a.L) * t, a.C + (b.C - a.C) * t, a.h + dh * t, 'mix');
}
export function ramp(a: Color, b: Color, n: number): Color[] {
  const out: Color[] = [];
  for (let i = 0; i < n; i++) out.push(mix(a, b, i / (n - 1)));
  return out;
}

/* PRNG determinista: la misma semilla devuelve el mismo fondo. */
export function rng32(seed: number): Rng {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
